<script lang="ts">
  import {
    formatGrowth,
    formatTicks,
    groupDigits,
    growthExponent,
    inputTooLargeNotice,
    type MeterPoint,
  } from '../../lib/complexity';
  import { pointStatus, toChartSeries, type ResultSeries } from './chart-model';
  import MeterChart from './MeterChart.svelte';
  import MeterTable from './MeterTable.svelte';
  import Pow from './Pow.svelte';

  /** 実測の表示（ステップ数が主、実行時間が補助。complexity-meter.md §1）。 */
  interface Props {
    series: ResultSeries[];
    measuring?: boolean;
    /** 2 本を別々のリクエストで測った（complexity-meter.md §4 の例外）。 */
    separate?: boolean;
  }

  let { series, measuring = false, separate = false }: Props = $props();
  let table = $state(false);

  const xMax = $derived(Math.max(1, ...series.flatMap((s) => s.points.map((p) => p.n))));
  const steps = $derived(series.map((s) => toChartSeries('steps', s)));
  const duration = $derived(series.map((s) => toChartSeries('duration', s)));

  function lastDone(points: readonly MeterPoint[]): MeterPoint | undefined {
    const done = points.filter(
      (p) => p.ticks !== null && pointStatus(p) === 'ok' && !p.ticksLowerBound,
    );
    return done[done.length - 1];
  }

  function growthLine(points: readonly MeterPoint[]): string | null {
    const done = points.filter(
      (p): p is MeterPoint & { ticks: number } =>
        p.ticks !== null && pointStatus(p) === 'ok' && !p.ticksLowerBound,
    );
    const a = done[done.length - 2];
    const b = done[done.length - 1];
    if (!a || !b) return null;
    return formatGrowth(a.n, a.ticks, b.n, b.ticks);
  }

  function skippedNotes(s: ResultSeries): string[] {
    const big = s.skipped.filter((k) => k.reason === 'input-too-large').map((k) => k.n);
    const slow = s.skipped.filter((k) => k.reason === 'predicted-too-slow').map((k) => k.n);
    const out: string[] = [];
    if (big.length > 0) out.push(inputTooLargeNotice(big));
    if (slow.length > 0) {
      out.push(
        `見積もりで 10^10 回を超える N = ${slow.map(groupDigits).join(', ')} は、3 秒で終わらないので送っていません。`,
      );
    }
    return out;
  }
</script>

<div class="results" aria-busy={measuring}>
  <ul class="steps" aria-label="ステップ数">
    {#each series as s (s.key)}
      {@const done = lastDone(s.points)}
      {@const g = growthExponent(
        s.points.filter((p) => pointStatus(p) === 'ok' && !p.ticksLowerBound),
      )}
      {@const growth = growthLine(s.points)}
      <li>
        <span class="who"><span class="key k{s.key}" aria-hidden="true"></span>{s.label}</span>
        {#if done && done.ticks !== null}
          <span class="text-soft small">N = {groupDigits(done.n)} で</span>
          <span class="count num" data-testid="steps-count-{s.key}"
            ><Pow text={formatTicks(done.ticks)} /> 回</span
          >
          {#if growth}<span class="small">{growth}</span>{/if}
          {#if g}<span class="small text-soft">回数の伸び: <Pow text={g.display} /></span>{/if}
        {:else}
          <span class="text-soft small">完了したケースがありません。</span>
        {/if}
        {#each skippedNotes(s) as note (note)}
          <span class="small text-soft note"><Pow text={note} /></span>
        {/each}
      </li>
    {/each}
  </ul>

  <div class="view-toggle">
    <button
      type="button"
      class="btn btn-sm"
      aria-pressed={table}
      data-testid="meter-table-toggle"
      onclick={() => (table = !table)}>{table ? 'グラフで見る' : '表で見る'}</button
    >
  </div>

  {#if table}
    <MeterTable
      caption="N ごとのステップ数と実行時間。TLE は 3 秒以内に終わらなかった N、≥ は打ち切られるまでに数えた回数"
      {series}
    />
  {:else}
    <div class="charts">
      <MeterChart
        kind="steps"
        title="N ごとのステップ数（viz.tick() の回数）"
        series={steps}
        {xMax}
        dimmed={measuring}
      />
      <MeterChart
        kind="duration"
        title="N ごとの実行時間（ms）"
        series={duration}
        {xMax}
        dimmed={measuring}
      />
    </div>
  {/if}
  <p class="text-soft small">
    実行時間は起動時間を含む値で、1 ケース 3 秒が上限です。N
    が小さいうちは起動時間が大半を占めるため、グラフには N = 10,000
    以上を描いています。最適化で目安（1 秒で約 10<sup>8</sup>
    回）より速く出ることがあります。{#if separate}2 本の線は別々に測った値です。{/if}
  </p>
</div>

<style>
  .results {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }
  .steps {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: var(--space-3);
  }
  .steps li {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-1) var(--space-3);
  }
  .who {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-weight: var(--fw-semibold);
    min-width: 7rem;
  }
  .count {
    font-size: var(--fs-xl);
    font-weight: var(--fw-heavy);
  }
  .note {
    flex-basis: 100%;
  }
  .small {
    font-size: var(--fs-sm);
  }
  .key {
    display: inline-block;
    width: 14px;
    height: 2px;
    border-radius: 1px;
  }
  .k1 {
    background: var(--series-1);
  }
  .k2 {
    background: var(--series-2);
  }
  .charts {
    display: grid;
    gap: var(--space-5);
  }
  @media (min-width: 1024px) {
    .charts {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    }
  }
</style>
